import React from "react";
import styled, { css } from "styled-components";
import { withTranslation, WithTranslation } from "react-i18next";
import MediaQuery from "react-responsive";
import images from "src/images";
import { media, desktopWidth, mobileWidth } from "src/utils/media";
import { colors } from "src/styles/colors";
import { lang } from "moment";

type Props = WithTranslation;

const Container = styled.div`
  display: flex;
  background-color: ${colors.bg.white.main};
  min-width: 1440px;
  padding-top: 214px;
  padding-bottom: 202px;
  ${media.mobile`
    min-width: auto;
    padding-top: 156px;
    padding-bottom: 108px;
  `}
`;

const Inner = styled.div<{ $isKo: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 1268px;
  margin: auto;
  ${media.mobile`
    justify-content: center;
    flex-wrap: wrap;
    max-width: 300px;
    width: 375px;
  `}
  ${({ $isKo }) =>
    $isKo
      ? css`
          ${media.mobile`
    & img:nth-child(2n + 1) {
      margin-right: 20px;
    }
    & img:last-child {
      margin-right: 0px;
    }
    `}
        `
      : css`
          ${media.mobile`
       & img:nth-child(2n) {
            margin-right: 20px;
          }
    `}
        `}
`;

const Row = styled.div`
  display: flex;
  flex: 1;
  flex-direction: row;
`;

const EmptyRow = styled(Row)`
  ${media.mobile`
    width: 100%;
    height: 20px;
    padding-top: 20px;
  `}
`;

const Logo = styled.img`
  height: 44px;
  ${media.mobile`
    height: 36px;
    margin-top: 10px;
    margin-bottom: 10px;
  `}
`;

const KBBankLogo = styled(Logo)`
  width: 157px;
  ${media.mobile`
    width: 141px;
  `}
`;

const KBBankEnLogo = styled(Logo)`
  width: 240px;
  ${media.mobile`
    width: 215px;
  `}
`;

const HashedLogo = styled(Logo)`
  width: 100px;
  ${media.mobile`
    width: 90px;
  `}
`;

const ShinhanLogo = styled(Logo)`
  width: 115px;
  ${media.mobile`
    width: 103px;
  `}
`;

const ShinhanEnLogo = styled(Logo)`
  width: 184px;
  ${media.mobile`
    width: 165px;
  `}
`;

const KodaLogo = styled(Logo)`
  width: 108px;
  ${media.mobile`
    width: 97px;
  `}
`;

const BithumbLogo = styled(Logo)`
  width: 122px;
  ${media.mobile`
    width: 110px;
  `}
`;

const GdacLogo = styled(Logo)`
  width: 108px;
  ${media.mobile`
    width: 97px;
  `}
`;

const IndodaxLogo = styled(Logo)`
  width: 151px;
  ${media.mobile`
    width: 97px;
  `}
`;

const FintechLogo = styled(Logo)`
  width: 106px;
  ${media.mobile`
    width: 96px;
  `}
`;

type LogoType =
  | "kbBank"
  | "hashed"
  | "shinhan"
  | "koda"
  | "bithumb"
  | "gdac"
  | "indodax"
  | "hashed"
  | "fintech";

const renderLogoByLanguageAndType = (language: string, type: LogoType) => {
  const byLanguage: Record<
    LogoType,
    { KoLogo: React.ReactNode; EnLogo: React.ReactNode }
  > = {
    kbBank: {
      KoLogo: (
        <KBBankLogo src={images.kbBankLogoSrc} srcSet={images.kbBankLogo} />
      ),
      EnLogo: (
        <KBBankEnLogo
          src={images.kbBankEnLogoSrc}
          srcSet={images.kbBankEnLogo}
        />
      ),
    },
    shinhan: {
      KoLogo: (
        <ShinhanLogo
          src={images.shinhanBankLogoSrc}
          srcSet={images.shinhanBankLogo}
        />
      ),
      EnLogo: (
        <ShinhanEnLogo
          src={images.shinhanBankEnLogoSrc}
          srcSet={images.shinhanBankEnLogo}
        />
      ),
    },
    bithumb: {
      KoLogo: (
        <BithumbLogo src={images.bithumbLogoSrc} srcSet={images.bithumbLogo} />
      ),
      EnLogo: (
        <BithumbLogo src={images.bithumbLogoSrc} srcSet={images.bithumbLogo} />
      ),
    },
    gdac: {
      KoLogo: <GdacLogo src={images.gdacLogoSrc} srcSet={images.gdacLogo} />,
      EnLogo: <GdacLogo src={images.gdacLogoSrc} srcSet={images.gdacLogo} />,
    },
    indodax: {
      KoLogo: (
        <IndodaxLogo src={images.indodaxLogoSrc} srcSet={images.indodaxLogo} />
      ),
      EnLogo: (
        <IndodaxLogo src={images.indodaxLogoSrc} srcSet={images.indodaxLogo} />
      ),
    },
    fintech: {
      KoLogo: (
        <FintechLogo src={images.fintechLogoSrc} srcSet={images.fintechLogo} />
      ),
      EnLogo: (
        <FintechLogo src={images.fintechLogoSrc} srcSet={images.fintechLogo} />
      ),
    },
    hashed: {
      KoLogo: (
        <HashedLogo src={images.hashedLogoSrc} srcSet={images.hashedLogo} />
      ),
      EnLogo: (
        <HashedLogo src={images.hashedLogoSrc} srcSet={images.hashedLogo} />
      ),
    },
    koda: {
      KoLogo: <KodaLogo src={images.kodaLogoSrc} srcSet={images.kodaLogo} />,
      EnLogo: <KodaLogo src={images.kodaLogoSrc} srcSet={images.kodaLogo} />,
    },
  };
  if (language === "ko") {
    return byLanguage[type].KoLogo;
  }
  return byLanguage[type].EnLogo;
};

const PartnerSection = (props: Props) => {
  const { i18n } = props;
  const language = i18n.language;
  const isKo = language === "ko";
  return (
    <Container>
      <Inner $isKo={isKo}>
        {renderLogoByLanguageAndType(language, "kbBank")}
        {renderLogoByLanguageAndType(language, "shinhan")}
        {renderLogoByLanguageAndType(language, "bithumb")}
        {renderLogoByLanguageAndType(language, "indodax")}
        {renderLogoByLanguageAndType(language, "hashed")}
        {renderLogoByLanguageAndType(language, "fintech")}
        {renderLogoByLanguageAndType(language, "koda")}
      </Inner>
    </Container>
  );
};

export default withTranslation()(PartnerSection);
